<template>
  <!--    Grid Header-->
  <div
    v-if="columnNames"
    class="cart-grid hidden gap-4 border-b py-2 text-sm font-medium xl:mb-6 xl:grid"
  >
    <div>{{ cartLabels.tableProduct }}</div>
    <div class="col-start-3 text-center">{{ sharedLabels.tableQuantity }}</div>
    <div class="col-start-4 text-end">{{ cartLabels.tableTotal }}</div>
    <div></div>
  </div>

  <!--      Default Card Layout with simple variant_  -->
  <div class="cart-grid align-start relative grid">
    <NuxtLink
      v-if="!isEshopSet"
      :to="pdpUrl"
      class="col-span-4 row-span-3 xl:col-span-1 xl:row-span-6"
    >
      <ProductImage
        v-if="!isEshopSet"
        :product-media="thumbnailUrl"
        :product-name="cartItem?.label"
        fallback-class="w-full h-full object-center rounded-lg bg-light-grey aspect-[74/100] p-4"
        main-class="product-image object-contain object-center rounded-lg bg-light-grey aspect-[74/100]"
        src-set-until="800"
      />
    </NuxtLink>
    <div
      :class="[
        { 'col-span-9 xl:col-span-2': isEshopSet, 'col-span-8 xl:col-span-4': childVariant },
      ]"
      class="col-span-5 flex flex-col gap-1 hyphens-auto xl:col-span-1"
    >
      <Tag v-if="isSubscriptionProduct" class="self-start" color="highlight">
        <CommonLabel path="BWF/shop/productDetailPage/subscriptionProduct/subscriptionLabel" />
      </Tag>
      <NuxtLink :to="pdpUrl">
        <Heading
          size="small"
          weight="normal"
          class="product-title"
          tag="h3"
          data-testid="cart-product-name"
        >
          {{ cartItem.children?.[0]?.label ?? cartItem.label }}
        </Heading>
      </NuxtLink>
      <Tint class="space-y-1 text-sm" tag="div">
        <p v-if="productNumber">
          <CommonLabel path="BWF/shop/productDetailPage/productNumber" />
          {{ productNumber }}
        </p>
        <p v-if="isSubscriptionProduct">
          <CommonLabel
            path="BWF/shop/productDetailPage/subscriptionProduct/subscriptionIntervalDropdownLabel"
          />:
          {{ cartItem?.label }}
        </p>
        <div v-if="itemOptions" class="product-options">
          <template v-for="option in itemOptions" :key="(option as PropertyGroupOptionCart).group">
            {{ (option as PropertyGroupOptionCart).option }}<br />
          </template>
        </div>
      </Tint>
      <Tint v-if="cartItem?.price?.referencePrice?.unitName" class="text-sm">
        <span
          >{{ cartItem?.price?.referencePrice?.purchaseUnit }}
          {{ cartItem?.price?.referencePrice?.unitName }}&nbsp;</span
        >(<FormattedCurrency :value="cartItem?.price?.referencePrice?.price" />&nbsp;/&nbsp;{{
          cartItem?.price?.referencePrice?.referenceUnit
        }}
        {{ cartItem?.price?.referencePrice?.unitName }})</Tint
      >
    </div>
    <div v-if="!childVariant" class="col-span-3 ml-auto flex flex-col items-end xl:col-span-1">
      <SharedPrice
        v-if="cartItem.price?.totalPrice >= 0 && !isExchangeBox"
        class="product-total-price"
        :class="{
          'hidden xl:block': childVariant,
          'font-medium': !childVariant,
        }"
        :value="cartItem.price?.totalPrice"
        :discounted="!childVariant && !!isListPrice"
        has-asterisk
      />
      <SharedPrice
        v-if="!childVariant && !!isListPrice"
        :value="totalListPrice"
        has-asterisk
        strikethrough
      />
      <Tint v-if="hasDiscount" tag="div">
        (-<FormattedPercent :value="discountPercentage" :precision="0" />)
      </Tint>
    </div>
    <CheckoutCartItemExchangeBoxChildren
      v-if="isExchangeBox"
      :cart-item="cartItem"
      class="col-span-full col-start-5 row-start-2 grid auto-rows-min grid-cols-[2fr_1fr] text-sm xl:contents"
    />

    <ul v-if="isEshopSet" class="col-span-full grid gap-2">
      <!--          NESTED PRODUCTS-->
      <li v-for="(child, index) in cartItem.children" :key="child.id">
        <CheckoutCartItem
          v-if="!isChildHidden(index)"
          :cart-item="child"
          :child-variant="true"
          :parent-pdp-url="pdpUrl"
        />
      </li>
    </ul>

    <div
      v-if="!childVariant"
      :class="{ 'invisible h-0': isExchangeBox || isSubscriptionProduct }"
      class="product-amount col-span-9 w-full text-sm xl:col-span-1 xl:col-start-3 xl:row-start-1"
    >
      <SelectField
        v-model="quantity"
        :disabled="maxQty > 1 ? null : true"
        :options="quantityOptions"
        :readonly="maxQty > 1 ? null : true"
        name="quantity"
      />
    </div>

    <div
      v-if="!childVariant"
      :class="[isSubscriptionProduct ? 'col-span-full' : 'col-span-3']"
      class="product-remove self-center justify-self-end xl:col-span-1 xl:col-start-5 xl:row-start-1 xl:self-start"
    >
      <button
        class="-mr-3 -mt-2 flex h-11 w-11 items-center justify-center text-black"
        :aria-label="cartLabels.removeArticleAriaLabel"
        @click="removeCartItem"
      >
        <SvgIcon
          class="text-link h-5 w-5 text-black"
          :class="{ 'animate-pulse': isLoading }"
          name="Remove-Item"
          type="button"
        />
      </button>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed } from "vue";
import type { LineItem, PropertyGroupOptionCart } from "@shopware-pwa/types";
import { Tag } from "~/components/clarity-ui/Tag";
import { Tint } from "~/components/clarity-ui/Tint";
import { FormattedCurrency } from "~/components/ui/FormattedCurrency";
import { FormattedPercent } from "~/components/ui/FormattedPercent";
import SharedPrice from "~/templates/components/shop/shared/SharedPrice.vue";
import SvgIcon from "~/templates/elements/SvgIcon.vue";
import SelectField from "~/templates/elements/form/SelectField.vue";
import useCommonLabels from "~/composables/useCommonLabels";
import CommonLabel from "~/templates/components/CommonLabel.vue";
import CheckoutCartItemExchangeBoxChildren from "~/templates/components/shop/checkout/CheckoutCartItemExchangeBoxChildren.vue";
import ProductImage from "~/templates/elements/ProductImage.vue";
import useShopwareNotifications from "~/composables/useShopwareNotifications";
import { useProductQuantityOptions } from "~/composables/shop/useProductBulkPrices";
import { Heading } from "~/components/clarity-ui/Heading";

export interface CheckoutCartItemProps {
  cartItem: LineItem;
  columnNames?: boolean;
  childVariant?: boolean;
  parentPdpUrl?: string;
}

const props = defineProps<CheckoutCartItemProps>();
const isLoading = ref(false);

const { pushShopwareError } = useShopwareNotifications();
const { getLabels } = useCommonLabels();
const cartLabels = computed(() => getLabels("BWF/shop/checkout/cart"));
const sharedLabels = computed(() => getLabels("BWF/shop/shared"));

const { cartItem } = toRefs(props);
const { itemOptions, removeItem, itemQuantity, changeItemQuantity } = useCartItem(cartItem);

const quantity = ref(0);
syncRefs(itemQuantity, quantity);

const { quantityOptions, maxQty } = useProductQuantityOptions(cartItem.value);

const updateQuantity = async (updatedQuantity: number | undefined) => {
  if (updatedQuantity === itemQuantity.value) {
    return;
  }

  isLoading.value = true;
  try {
    await changeItemQuantity(Number(updatedQuantity));
    if (quantity.value !== itemQuantity.value) {
      quantity.value = Number(itemQuantity.value);
    }
  } catch (e) {
    await pushShopwareError(e);
  }
  isLoading.value = false;
};

const thumbnailUrl = computed(() => {
  if (cartItem.value.cover) {
    return cartItem?.value?.cover;
  }

  return cartItem.value?.children?.[0]?.cover;
});

const isEshopSet = computed(
  () => cartItem?.value?.extensions?.hasOwnProperty("eshopSetData") ?? false,
);
const isExchangeBox = computed(
  () => cartItem?.value?.children?.[0]?.extensions?.hasOwnProperty("tauschbox") ?? false,
);
const isSubscriptionProduct = computed(
  () => cartItem?.value?.children?.[0]?.extensions?.hasOwnProperty("subscription") ?? false,
);

const isListPrice = computed(
  () =>
    cartItem.value?.children?.[0]?.priceDefinition?.listPrice || cartItem?.value?.price?.listPrice,
);
const hasDiscount = computed(() => {
  return cartItem.value?.price?.listPrice?.percentage;
});

const discountPercentage = computed(() => {
  return Math.round(cartItem.value?.price?.listPrice?.percentage || 0);
});

/*
 * checks whether a child is the first item AND should be hidden
 * OR
 * ist a hidden Item/ SKU
 * */
const isChildHidden = (index: number) => {
  const child = cartItem.value?.children?.[index];
  return !!(
    ((index === 0 &&
      (cartItem?.value?.extensions?.brita_eshop_set_hide_main_product_image?.value ?? false)) ||
      child?.extensions?.eshopSet?.eshopSetProductIsHidden) ??
    false
  );
};
// TODO: only temporary, rebuild if backend provides an extra property for this.
//@ts-ignore
const totalListPrice = computed(
  () =>
    cartItem?.value?.children?.[0]?.priceDefinition?.listPrice ??
    cartItem?.value?.price?.listPrice?.price * (cartItem?.value?.price?.quantity ?? 0),
);
const productNumber = computed(() => {
  if (isSubscriptionProduct.value) {
    //@ts-ignore
    return cartItem.value?.children?.[0]?.payload?.productNumber;
  } else {
    //@ts-ignore
    return cartItem.value?.payload?.productNumber;
  }
});

watch(quantity, () => updateQuantity(quantity.value));

const { trackItemRemoveFromCart } = useCheckoutTracking();
const removeCartItem = async () => {
  isLoading.value = true;
  // track before remove, otherwise item is gone in the next tick
  trackItemRemoveFromCart(cartItem);
  try {
    await removeItem();
  } catch (e) {
    await pushShopwareError(e);
  }
  isLoading.value = false;
};

const { getPdpUrlByProduct, getEShopSetUrlByProduct, hasConfiguredPdp } = useMagnoliaShopPages();

const pdpUrl = computed(() => {
  let mainProduct = {};
  let pathVariable = null;
  if (props.parentPdpUrl) {
    // nested items, recursive rendering. choose parents urls
    return toRaw(props.parentPdpUrl);
  } else if (isEshopSet.value) {
    // set url search payload
    return getEShopSetUrlByProduct(cartItem?.value);
    // @ts-ignore
  } else if (isSubscriptionProduct.value) {
    // set url search payload
    // subscription main product is nested in the first childs payload
    mainProduct = cartItem?.value?.children[0]?.payload;
    // @ts-ignore
    pathVariable = mainProduct?.productNumber ?? mainProduct?.productId;
  } else if (isExchangeBox.value) {
    // set url search payload
    mainProduct = cartItem?.value?.children[0];
    if (hasConfiguredPdp(cartItem?.value?.children[0])) {
      // will look for pdps associated directly with variants
      mainProduct = cartItem?.value?.children[0];
    } else {
      // will look for pdps associated with the main product & fallback
      mainProduct = cartItem?.value?.children[0]?.payload;
    }
    // @ts-ignore
    pathVariable = mainProduct?.productNumber || mainProduct?.productId;
  }
  // @ts-ignore
  else if (cartItem.value?.payload?.parentId || cartItem.value?.payload?.referencedId) {
    // variant urls
    mainProduct = cartItem?.value?.payload;
    pathVariable = cartItem.value?.id;
  } else {
    // no variant url
    mainProduct = cartItem?.value;
  }
  return getPdpUrlByProduct(mainProduct, pathVariable);
});
</script>

<style>
.cart-grid {
  @apply grid-cols-12 gap-4 xl:grid-cols-5 xl:gap-y-0.5;

  @screen xl {
    grid-template-columns: 3fr 5fr 1.2fr 2fr 1fr;
  }
}
</style>
